
import { WrapResponse } from '@/types';
import { StateSlice } from '@/stores/appStore';
import { snakeToCamel } from '@/lib/utils';
import toast from 'react-hot-toast';
import { api, host } from '@/lib/env';
import { fun } from '@/lib/request';
import * as Icons from "@/components/fun-ui/icons";
import { SearchHistoryItem } from '@/components/paper-search/types';

export type PaymentType = 'upgrade' | 'recharge';
export type GlobalConfig = {
  // 论文最大上传数量
  maxSummaryCount: number;
  // Plus会员，论文最大上传数量，默认100篇
  maxVipSummaryCount: number;
  pdfMaxSize: number;
  contactImageUrl: string;
  paymentTabs: {
    value: PaymentType;
    label: string;
  }[];
  pc: {
    // PC中监测移动端访问
    mobileDetectImageUrls: string[];
    // 营销
    marketing: {
      starting: Boolean;
      entranceImageUrl: string;
      entranceFoldImageUrl: string;
      marketingImageUrl: string;
      marketingImage2Url: string;
      marketingSurveyUrl: string;
      // v2 图片数组 2501版本
      marketingImagesV2501: string[] | {
        html: string;
        image: string;
      }[];
      marketingBackgroundColor: string;
      // 定向人群展示：'all' 全部会员（默认），'normal' 仅普通会员，'vip' 仅VIP
      targetAudience?: 'all' | 'normal' | 'vip';
    },
    //产品站
    literatureReview: {
      title: string;
      subtitle: string;
      url: string;
      features: {
        title: string;
        caption: string;
        url: string;
      }[];
    },
    //产品站
    literatureTable: {
      title: string;
      subtitle: string;
      backgroundImage: string;
      features: {
        title: string;
        caption: string;
        url: string;
      }[];
    }
  },
  traceOriginAutoSendMessageTemplate: string;
};

export type AssistantsInputSchema = {
  key: string;
  label: string;
  value?: string;
  requiredMsg?: string;
  component: {
    name: string;
    enum?: {
      value: string;
      label: string;
    }[];
    maxLength?: number;
    placeholder?: string;
  };
  shown?: string;
}[];

export type AssistantsOutputSchema = {
  label: string;
  value?: string;
};
export type AssistantsSchema = {
  value: string;
  label: string;
  input: AssistantsInputSchema;
  output: AssistantsOutputSchema;
}[];
export type AssistantsAIGeneratedData = {
  content: string;
}


export type BadgeType = 'none' | 'pure' | 'normal' | 'primary' | 'hyper';
export type LadderPrice = {
  price: number;
  coins: number;
  label: string;
  desc: string;
  badge: BadgeType;
  // vip 开通
  vip: {
    coins: number;
    label: string;
    desc: string;
    badge: BadgeType;
    vipDesc: string;
  },
  // 场景
  stage: ('pricing' | 'wx' | 'pc')[];
  users: ('normal' | 'plus')[];
}

export type PaymentRightsItem = {
  label: string;
  tip?: string;
  badge?: string;
  icon?: keyof typeof Icons | '';
  linkto?: string;
  isDesc?: boolean;
  isNew?: boolean;
}

export type LadderVipPayment = {
  label: string;
  days: number;
  price: number;
  priceDiscountedPerMonth: number;
  priceOriginalPerMonth: number;
  desc: string;
  badge: BadgeType;
  rightsList: PaymentRightsItem[];
}

export type UpgradeVipFeature = {
  label: string;
  title: string;
  desc: string;
  button: {
    type: string;
    label: string;
  },
  vipTabas: boolean;
  rightsList: PaymentRightsItem[];
}

export type PaymentUpgradeSet = {
  title: string;
  features: UpgradeVipFeature[];
  items: LadderVipPayment[];
}

export type FollowUsItem = {
  qrCode: string;
  account: string;
  label: string;
  icon: string;
  url?: string;
}

export type DidYouKnowConfigItem = {
  title: string;
  content: string;
  tag: string[];
}

export type DidYouKnowConfig = {
  title: string;
  items: DidYouKnowConfigItem[];
}

export type CommonProblem = {
  title: string;
  content: string;
  tag: string[];
}

export type SystemAnnouncement = {
  // 公告内容，支持markdown，链接会自动转换为外部链接
  content: string;
  // 公告的页面路径，用于控制展示公告的页面
  // wx:home; wx:summary; wx:workfllow; pc:home; pc:papers; ...
  pages: string[];
}[];

export type SystemMaintenance = {
  content: string;
  title: string;
  maintenance: boolean;
  // wx:home; wx:summary; wx:workfllow; pc:home; pc:papers; ...
  whitelist: string[];
};

export type FeedbackKeys = 'score' | 'suggestions' | 'content' | 'contact' | 'summaryScore';
export type FeedbackScene = 'default' | 'assistants' | 'summary' | 'translation' | 'aiCol' | 'aiColCell' | 'aiColBasicCell' | 'paperSearch';
export type Feedback = {
  title: Record<FeedbackScene, string>;
  scene: Record<FeedbackScene, string[]>;
  score: {
    key: "score";
    label: string;
    required: boolean;
    options: {
      emoji: string;
      label: string;
      score: number;
      maxSelection: number;
      suggestions: string[];
      style: {
        borderColor: string;
        backgroundColor: string;
        color: string;
      }
    }[]
  };
  summaryScore: {
    key: "score";
    label: string;
    required: boolean;
    options: {
      emoji: string;
      label: string;
      score: number;
      maxSelection: number;
      suggestions: string[];
      style: {
        borderColor: string;
        backgroundColor: string;
        color: string;
      }
    }[]
  };
  suggestions: {
    key: "suggestions";
    required: boolean;
    label: string;
  };
  content: {
    key: "content";
    required: boolean;
    maxLength: number;
    placeholder: string;
  };
  contact: {
    key: "contact";
    required: boolean;
    label: string;
    tip: string;
  };
} & {
  [key: string]: {
    key: string;
    [key: string]: any;
  }
};

export type AIColsConfig = {
  // 最大可定义数量
  customLimit: number;
  // vip 最大可定义数量
  customVipLimit: number;
  // 最大可开启的列数量
  aiColsLimit: number;
  // vip最大可开启的列数量
  aiColsVipLimit: number;
  // 预设AI列价格
  presetPrice: number;
  // 自定义AI列价格
  customPrice: number;
  // 智能解析价格
  chartPrice: number;
  // 用户信
  letter: {
    width: number;
    height: number;
    url: string;
    tip: string;
  };
}

export type EventLogType = {
  platform?: 'web' | 'cx';
  element: string;
  data?: Record<string, any>;
  event?: 'click';
  path?: string;
}
interface CommonFetchable<T> {
  value: T | null;
  loading: boolean;
  fetch: () => Promise<void>;
}

type ChapterItemRange = {
  range: [number, number];
}

type ChapterItemUnit = {
  unit: '字' | '词' | '';
}

type ChapterItemRangeUnit = ChapterItemRange & ChapterItemUnit;

export type DefaultChapterItem = {
  title: string;
  subTitle: string;
  level: number;
} & ChapterItemRange;
export type ChapterTocConfig = {
  defaultChapters: Record<string, Record<string, {
    header: DefaultChapterItem[];
    footer: DefaultChapterItem[];
  }>>;
  generalChapters: Record<string, Record<string, ChapterItemRangeUnit>>
};

export type PaperSearchDomainConfig = {
  key: string;
  label: string;
  contents: (string | SearchHistoryItem)[];
};

export type PaperSearchFilterConfig = {
  yearPresets: Array<{
    label: string;
    years?: number;
    custom?: boolean;
  }>;
  types: Array<{
    key: string;
    label: string;
  }>;
  yearRange: {
    min: number;
    max: "current" | number;
  };
};

export type PaperSearchConfig = {
  filters: PaperSearchFilterConfig;
  domains: PaperSearchDomainConfig[];
  paperTypes: Array<{
    key: string;
    label: string;
  }>;
};

function initialCommonFetchable<T>(
  key: keyof CommonFetchSlice,
  name: string,
  get: () => CommonSlice,
) {
  return {
    value: null,
    loading: false,
    fetch: () => get().commonFetch(key, name),
  };
}

export type CommonFetchSlice = {
  // 获取梯度价格
  ladderPrices: CommonFetchable<LadderPrice[]>;
  // 获取梯度价格
  paymentUpgradeSet: CommonFetchable<PaymentUpgradeSet>;
  // 获取常见问题
  commonProblems: CommonFetchable<CommonProblem[]>;
  // 获取全局配置
  globalConfig: CommonFetchable<GlobalConfig>;
  // 获取关注我们列表
  followUsList: CommonFetchable<FollowUsItem[]>;
  // 获取短链接映射
  shortUrlMap: CommonFetchable<Record<string, string>>;
  // 获取你知道吗配置
  didYouKnowConfig: CommonFetchable<DidYouKnowConfig>;
  // SystemAnnouncement 系统公告
  systemAnnouncement: CommonFetchable<SystemAnnouncement> & {
    content: string;
    getContent: (path: string) => Promise<string>;
  };
  systemMaintenance: CommonFetchable<SystemMaintenance>;
  feedback: CommonFetchable<Feedback>;
  aiColsConfig: CommonFetchable<AIColsConfig>;
  chapterTocConfig: CommonFetchable<ChapterTocConfig>;
  // 获取论文搜索配置
  paperSearchConfig: CommonFetchable<PaperSearchConfig>;
};

export type CommonSlice = {
  assistants: AssistantsSchema | null;
  assistantsOutput: Record<string, AssistantsOutputSchema['value']>;
  updateAssistantsOutput: (tab: string, value: string) => void;
  assistantsAILoading: boolean;
  initialAssistants: () => Promise<void>;
  assistantsAIGenerated: (tab: string, assistantsInput: Record<string, string>) => Promise<void>;
  commonFetch: <T>(
    key: keyof CommonSlice,
    name: string,
  ) => Promise<void>;
  eventLog: (log: EventLogType) => Promise<void>;
} & CommonFetchSlice;


export const createCommonSlice: StateSlice<CommonSlice> = (set, get) => ({
  assistants: null,
  assistantsOutput: {},
  updateAssistantsOutput: (key, value) => {
    set(state => {
      state.assistantsOutput[key] = value;
    });
  },
  assistantsAILoading: false,
  initialAssistants: async () => {
    if (get().assistants) {
      return;
    }
    const responseData = await queryCommonConfig<AssistantsSchema>('learn_helper');
    if (responseData?.code === 0) {
      set(state => {
        state.assistants = snakeToCamel(responseData.data);
      });
    }
  },
  assistantsAIGenerated: async (tab, input) => {
    if (get().assistantsAILoading || !tab) {
      return;
    }
    set(state => {
      state.assistantsAILoading = true;
    });
    try {
      const { prompt_key, ...params } = input;
      const formData = new FormData();
      formData.append('prompt_key', prompt_key);
      Object.keys(params).forEach((key) => {
        formData.append(`params[${key}]`, params[key]);
      });
      const { code, data, message: msg } = (await fun.post<WrapResponse<AssistantsAIGeneratedData>>(
        api('/helper/complete', true),
        formData,
      )).data;
      if (code === 0) {
        const res = snakeToCamel<AssistantsAIGeneratedData>(data);
        get().updateAssistantsOutput(tab, res.content);
        get().refreshUserInfo();
      } else if (code === 5001) {
        get().setBalanceInsufficient(true);
      } else {
        toast.error(msg);
      }
    } catch (error) {
      console.error('Error query common/config of learn_helper:', error);
    } finally {
      set(state => {
        state.assistantsAILoading = false;
      });
    }
  },
  commonFetch: async <T extends Object>(
    key: keyof CommonSlice,
    name: string,
  ): Promise<void> => {
    const state = get();

    const commonState = state[key] as CommonFetchable<T>;

    // 检查是否已经有值或正在加载
    if (commonState.value || commonState.loading) {
      return;
    }

    // 开始加载
    set((state) => {
      (state[key] as CommonFetchable<T>).loading = true;
    });

    try {
      // 执行查询操作
      const responseData = await queryCommonConfig<T>(name);
      if (responseData?.code === 0) {
        // 设置查询结果
        set((state) => {
          (state[key] as CommonFetchable<T>).value = snakeToCamel<T>(responseData.data);
        });
      }
    } catch (error) {
      console.error(`Error fetching data for key ${key}:`, error);
      // 这里可以处理错误，例如设置一个错误状态
    } finally {
      // 结束加载
      set((state) => {
        (state[key] as CommonFetchable<T>).loading = false;
      });
    }
  },

  ladderPrices: {
    ...initialCommonFetchable<LadderPrice[]>('ladderPrices', 'ladder_prices_v2', get),
  },
  paymentUpgradeSet: {
    ...initialCommonFetchable<PaymentUpgradeSet>('paymentUpgradeSet', 'payment_upgrade_set', get),
  },
  commonProblems: {
    ...initialCommonFetchable<CommonProblem[]>('commonProblems', 'common_problems', get),
  },
  globalConfig: {
    ...initialCommonFetchable<GlobalConfig>('globalConfig', 'global_config', get),
  },
  shortUrlMap: {
    ...initialCommonFetchable<Record<string, string>>('shortUrlMap', 'short_map', get),
  },
  followUsList: {
    ...initialCommonFetchable<FollowUsItem[]>('followUsList', 'follow_us_list', get),
  },
  didYouKnowConfig: {
    ...initialCommonFetchable<DidYouKnowConfig>('didYouKnowConfig', 'did_you_know', get),
  },
  systemAnnouncement: {
    ...initialCommonFetchable<SystemAnnouncement>('systemAnnouncement', 'system_announcement', get),
    content: '',
    // path两种格式：微信路径 pages/XXXX/index；其他为pc路径（pc的home路径单独处理一下）
    async getContent(pathname) {
      await get().systemAnnouncement.fetch();
      let key = '';
      // 正则表达式匹配 'pages/' 开头，后跟任意非 '/' 字符，可能包含多个子目录，以 '/index' 结尾，可能包含查询参数和锚点
      const regex = /^pages\/([^\/]+(?:\/[^\/]+)*)\/index(?:\?[^#]*)?(?:#.*)?$/;
      const match = pathname.match(regex);
      if (match?.[1]) {
        key = `wx:${match[1]}`;
      } else {
        key = `pc:${pathname.replace(/^\//, '') || 'default'}`;
      }
      const items = get().systemAnnouncement.value || [];
      let content = '';
      items.forEach((item) => {
        const found = item.pages.some((page) => {
          return key === page || key.startsWith(`${page}/`);
        });
        if (found) {
          content = item.content || '';
        }
      });
      set(state => {
        state.systemAnnouncement.content = content;
      });
      return content;
    },
  },
  systemMaintenance: {
    ...initialCommonFetchable<SystemMaintenance>('systemMaintenance', 'system_maintenance', get),
  },
  feedback: {
    ...initialCommonFetchable<Feedback>('feedback', 'feedback', get),
  },
  aiColsConfig: {
    ...initialCommonFetchable<AIColsConfig>('aiColsConfig', 'ai_cols_config', get),
  },
  chapterTocConfig: {
    ...initialCommonFetchable<ChapterTocConfig>('chapterTocConfig', 'chapter_toc_config', get),
  },
  paperSearchConfig: {
    ...initialCommonFetchable<PaperSearchConfig>('paperSearchConfig', 'paper_search_config', get),
  },
  eventLog: async (log: EventLogType) => {
    try {
      const { data, code } = (await fun.post(
        api('/common/eventLog', true), {
        platform: log.platform ?? 'web',
        element: log.element,
        data: log.data,
        event: log.event ?? 'click',
        path: log.path ?? window.location.pathname,
      })).data;
      if (code === 0) {

      }
    } catch (error) {
      console.error('Error post eventLog:', error);
    } finally {

    }
  },
});

export async function queryCommonConfig<T extends Object>(key: string) {
  try {
    return (await fun.post<WrapResponse<T>>(
      api('/common/config', true),
      {
        key,
      },
    )).data;
  } catch (error) {
    console.error(`Error query common/config by ${key}`, error);
    return null;
  }
}